import { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbBookmarkPlus, TbCheck } from "react-icons/tb";
import { useDropDirection } from "src/hooks/useDropDirection";
import { fold, foldExpanded } from "src/helpers/textFold";
import type { JustificationList } from "src/hooks/useJustifications";
import { MAX_JUSTIFICATION_LENGTH } from "src/helpers/justifications";

/**
 * Begründung input over the practice's saved Begründungen (the same numbered list for every code):
 * focusing it lists them, typing words narrows the list, a number ("2") + Return inserts that one, ↑/↓ +
 * Return pick, Escape closes. Return never submits — the template editor is a form. Free text stays
 * possible; "Als Begründung speichern" adds it to the list.
 */
export function JustificationField({
    value,
    placeholder,
    justifications,
    onChange,
}: {
    value: string;
    placeholder: string;
    justifications?: JustificationList;
    onChange: (value: string) => void;
}) {
    const { t } = useTranslation();
    const listId = useId();
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [saveState, setSaveState] = useState<"saving" | "saved" | "failed" | null>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const items = justifications?.items ?? [];

    const trimmed = value.trim();
    const numbered = /^\d{1,3}$/.test(trimmed) ? items[Number(trimmed) - 1] : undefined;
    const known = items.some((j) => j.text.toLowerCase() === trimmed.toLowerCase());

    // Each option keeps its list number, also when filtered.
    const options = useMemo(() => {
        const all = items.map((j, i) => ({ ...j, n: i + 1 }));
        if (/^\d{1,3}$/.test(trimmed)) return all.filter((o) => String(o.n) === trimmed);
        if (known) return [];
        const tokens = trimmed.split(/\s+/).filter(Boolean);
        return all.filter((o) => tokens.every((tok) => fold(o.text).includes(fold(tok)) || foldExpanded(o.text).includes(foldExpanded(tok))));
    }, [items, trimmed, known]);

    const showList = open && options.length > 0;
    const direction = useDropDirection(fieldRef, showList, 260);
    const canSave = !!justifications?.add && !!trimmed && !known && !/^\d+$/.test(trimmed) && trimmed.length <= MAX_JUSTIFICATION_LENGTH;

    const pick = (text: string) => {
        onChange(text);
        setOpen(false);
        setActive(-1);
    };

    const save = async () => {
        if (!justifications?.add) return;
        setSaveState("saving");
        try {
            await justifications.add(trimmed);
            setSaveState("saved");
            window.setTimeout(() => setSaveState(null), 3000);
        } catch {
            setSaveState("failed");
        }
    };

    return (
        <div className="goa-line-editor__code-field goa-line-editor__justification" ref={fieldRef}>
            <input
                type="text"
                className="goa-line-editor__field-input"
                placeholder={placeholder}
                value={value}
                role="combobox"
                aria-expanded={showList}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
                onChange={(e) => {
                    onChange(e.target.value);
                    setOpen(true);
                    setActive(-1);
                    setSaveState(null);
                }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && options.length > 0) {
                        e.preventDefault();
                        setOpen(true);
                        setActive((i) => Math.min(i + 1, options.length - 1));
                    } else if (e.key === "ArrowUp" && options.length > 0) {
                        e.preventDefault();
                        setActive((i) => Math.max(i - 1, -1));
                    } else if (e.key === "Enter") {
                        e.preventDefault();
                        if (showList && active >= 0) pick(options[active].text);
                        else if (numbered) pick(numbered.text);
                    } else if (e.key === "Escape" && showList) {
                        // Only swallow it while the list is open — otherwise Escape still closes a popup.
                        e.stopPropagation();
                        setOpen(false);
                        setActive(-1);
                    }
                }}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            />
            {showList && (
                <div className={`goa-line-editor__code-dropdown goa-line-editor__code-dropdown--wide${direction === "up" ? " goa-line-editor__code-dropdown--up" : ""}`}>
                    <ul id={listId} role="listbox" aria-label={t("goaLineEditor.justificationList")}>
                        {options.map((o, i) => (
                            <li key={o.id} id={`${listId}-${i}`} role="option" aria-selected={i === active} onMouseDown={() => pick(o.text)}>
                                <span className="goa-line-editor__code-dropdown-nr">{o.n}</span>
                                <span className="goa-line-editor__code-dropdown-desc">{o.text}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="goa-line-editor__dropdown-foot">
                        <span>{t("goaLineEditor.justificationNumberTip")}</span>
                        <a href="/settings?section=justifications" target="_blank" rel="noreferrer" onMouseDown={(e) => e.preventDefault()}>
                            {t("goaLineEditor.manageJustifications")}
                        </a>
                    </p>
                </div>
            )}
            {(canSave || saveState) && (
                <div className="goa-line-editor__justification-save" role="status">
                    {saveState === "saved" ? (
                        <span className="goa-line-editor__justification-saved"><TbCheck aria-hidden /> {t("goaLineEditor.justificationSaved")}</span>
                    ) : saveState === "failed" ? (
                        <span className="goa-line-editor__field-hint goa-line-editor__field-hint--error">{t("goaLineEditor.justificationSaveFailed")}</span>
                    ) : canSave ? (
                        <button type="button" className="goa-line-editor__justification-save-btn" onClick={() => void save()} disabled={saveState === "saving"}>
                            <TbBookmarkPlus aria-hidden /> {t("goaLineEditor.saveJustification")}
                        </button>
                    ) : null}
                </div>
            )}
        </div>
    );
}
